import React, { useEffect, useState } from 'react';
import { Star, CheckCircle, AlertCircle } from 'lucide-react';
import { vendorReviewApi } from '../services/api';
import { VendorReviewCreate } from '../types';
import { extractApiError } from '../utils/errorUtils';

/**
 * VendorReviewForm Component (Issue #956)
 *
 * Allow customers to submit vendor reviews.
 *
 * Features:
 * - Star rating selector (1-5 stars)
 * - Text area for review (max 1000 chars)
 * - Character counter
 * - "Verified Purchase" badge indicator
 * - Validation: requires reservation/pre-order with vendor
 * - Submit button with loading state
 * - Success/error messages
 */

interface VendorReviewFormProps {
  vendorId: number;
  fairId: number;
  onSuccess?: () => void;
}

const VendorReviewForm: React.FC<VendorReviewFormProps> = ({
  vendorId,
  fairId,
  onSuccess
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [eligibilityMessage, setEligibilityMessage] = useState('');
  const [verifiedPurchase, setVerifiedPurchase] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const MAX_CHARS = 1000;

  useEffect(() => {
    checkEligibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, fairId]);

  const checkEligibility = async () => {
    setCheckingEligibility(true);
    try {
      const data = await vendorReviewApi.checkReviewEligibility(vendorId, fairId);
      setEligible(data.eligible);
      setEligibilityMessage(data.reason);
      setVerifiedPurchase(data.verified_purchase);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to check review eligibility:', apiError);
      setEligible(false);
      setEligibilityMessage('Unable to verify eligibility. Please try again.');
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (reviewText.length > MAX_CHARS) {
      setError(`Review text must be ${MAX_CHARS} characters or less`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reviewData: VendorReviewCreate = {
        vendor_id: vendorId,
        fair_id: fairId,
        rating,
        review_text: reviewText.trim() || undefined
      };

      await vendorReviewApi.createReview(vendorId, reviewData);
      setSuccess(true);
      setRating(0);
      setReviewText('');

      // Call onSuccess callback after a brief delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 1500);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to submit review:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStarSelector = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isActive = i <= (hoverRating || rating);
      stars.push(
        <button
          key={i}
          type="button"
          onClick={() => setRating(i)}
          onMouseEnter={() => setHoverRating(i)}
          onMouseLeave={() => setHoverRating(0)}
          className="focus:outline-none focus:ring-2 focus:ring-dusty-teal rounded"
          data-testid={`star-${i}`}
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              isActive ? 'text-yellow-500 fill-current' : 'text-gray-300 fill-current'
            }`}
          />
        </button>
      );
    }
    return stars;
  };

  if (checkingEligibility) {
    return (
      <div className="text-center py-8 text-gray-500" data-testid="checking-eligibility">
        Checking eligibility...
      </div>
    );
  }

  if (!eligible) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-testid="not-eligible">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-yellow-900">Cannot Submit Review</h3>
            <p className="text-yellow-800 mt-1">{eligibilityMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="review-success">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-green-900">Review Submitted!</h3>
            <p className="text-green-800 mt-1">Thank you for your feedback.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="vendor-review-form">
      {/* Verified purchase indicator */}
      {verifiedPurchase && (
        <div className="flex items-center gap-2 bg-teal-50 text-primary-dark px-3 py-2 rounded">
          <CheckCircle className="w-5 h-5" />
          <span className="text-sm font-medium">
            This will be marked as a verified purchase
          </span>
        </div>
      )}

      {/* Star rating selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Rating <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-1" data-testid="star-selector">
          {renderStarSelector()}
        </div>
        {rating > 0 && (
          <p className="text-sm text-gray-600 mt-1" data-testid="rating-label">
            {rating} {rating === 1 ? 'star' : 'stars'}
          </p>
        )}
      </div>

      {/* Review text */}
      <div>
        <label htmlFor="review-text" className="block text-sm font-medium text-gray-700 mb-2">
          Review (optional)
        </label>
        <textarea
          id="review-text"
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          rows={5}
          maxLength={MAX_CHARS}
          placeholder="Share your experience with this vendor..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-dusty-teal"
          data-testid="review-text-input"
        />
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-500">
            Share details about your experience to help other customers
          </p>
          <p
            className={`text-xs ${
              reviewText.length > MAX_CHARS * 0.9 ? 'text-red-500' : 'text-gray-500'
            }`}
            data-testid="char-counter"
          >
            {reviewText.length} / {MAX_CHARS}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="error-message">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Submit button */}
      <div className="flex items-center justify-end gap-3">
        <button
          type="submit"
          disabled={loading || rating === 0}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="submit-button"
        >
          {loading ? 'Submitting...' : 'Submit Review'}
        </button>
      </div>
    </form>
  );
};

export default VendorReviewForm;
